// ============================================================
// PDF-ul cercetarii, facut in browser cu pdfmake (19.10.2026, cerut de Luminita ca sa trimita
// cercetarea direct pe WhatsApp: butonul „Trimite PDF” face fisierul si deschide fereastra de
// partajare a sistemului). Textul ramane text, linkurile la surse raman active.
// Aspectul urmeaza PDF-ul aprobat de ea pe tema 19 la Virgil (antet verde, „Pe scurt” in chenar,
// faptele cu eticheta „oficial”, riscurile pe galben, „Lasate deoparte” pe gri).
// Biblioteca se incarca doar la apasare (import dinamic), ca aplicatia sa nu se ingreuneze.
// ============================================================

import { t } from './i18n';

const VERDE = '#1A6B3C';
const VERDE_DESCHIS = '#E8F5EE';
const GRI_TEXT = '#4B5563';

const lista = (x) => (Array.isArray(x) ? x : []);
const https = (s) => (/^https:\/\//.test(s || '') ? s : null);

// Numele fisierului: fara diacritice si semne, ca sa treaca prin WhatsApp si Windows.
export function numePdf(client, nr) {
  const curat = String(client || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9 ]+/g, ' ').trim().replace(/\s+/g, ' ');
  return `Cercetare ${curat} tema ${nr}.pdf`;
}

function titlu(text, culoare = VERDE) {
  return [
    { text: text.toUpperCase(), fontSize: 8.5, bold: true, color: culoare, characterSpacing: 0.6, margin: [0, 10, 0, 2] },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.7, lineColor: '#D1E8DA' }], margin: [0, 0, 0, 5] },
  ];
}

function sursa(x) {
  if (!x.sursa && !x.link) return null;
  const link = https(x.link);
  const parti = [{ text: x.sursa || x.link, color: link ? VERDE : GRI_TEXT, link: link || undefined }];
  if (x.data) parti.push({ text: ` · ${x.data}`, color: GRI_TEXT });
  return { text: parti, fontSize: 8, margin: [0, 1, 0, 0] };
}

// Un chenar colorat in jurul unei sectiuni (riscuri, lasate deoparte).
function chenar(continut, fundal, margine) {
  return {
    table: { widths: ['*'], body: [[{ stack: continut, margin: [8, 2, 8, 6] }]] },
    layout: { fillColor: () => fundal, hLineColor: () => margine, vLineColor: () => margine, hLineWidth: () => (margine ? 0.7 : 0), vLineWidth: () => (margine ? 0.7 : 0) },
    margin: [0, 6, 0, 0],
  };
}

export function definitiePdf({ c, client, nr, titlu: titluTema }) {
  const k = c.continut || {};
  const data = c.gata_la ? new Date(c.gata_la).toLocaleDateString('ro-RO', { timeZone: 'Europe/Bucharest' }) : '';
  const pastile = [data,
    Number.isInteger(k.surse_oficiale) ? t('cercetare', 'nSurse', { n: k.surse_oficiale }) : '',
    t('cercetare', 'nFapte', { n: lista(k.fapte).length }),
    t('cercetare', 'nDeoparte', { n: lista(k.deoparte).length })].filter(Boolean);

  const C = [];
  C.push({ text: 'NEW MIND · NM SOCIAL', fontSize: 7.5, bold: true, color: VERDE, characterSpacing: 1.8 });
  C.push({ text: t('cercetare', 'antet', { client: client.nume, nr }), fontSize: 9.5, bold: true, color: '#374151', margin: [0, 5, 0, 2] });
  if (titluTema) C.push({ text: titluTema, fontSize: 16, bold: true, color: '#0B3D22', lineHeight: 1.1, margin: [0, 0, 0, 5] });
  C.push({ text: pastile.join('   ·   '), fontSize: 8, bold: true, color: '#14532D', margin: [0, 0, 0, 6] });
  C.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 2.5, lineColor: VERDE }], margin: [0, 0, 0, 4] });

  if (k.pe_scurt) {
    C.push(...titlu(t('cercetare', 'peScurt')));
    C.push({
      table: { widths: [3, '*'], body: [[{ text: '', fillColor: '#2E9E5B' }, { text: k.pe_scurt, fontSize: 10.5, margin: [8, 5, 8, 5], fillColor: '#F7FBF8' }]] },
      layout: 'noBorders',
    });
  }

  if (lista(k.fapte).length) {
    C.push(...titlu(t('cercetare', 'fapte')));
    C.push({
      ol: k.fapte.map(f => ({
        stack: [
          { text: [{ text: ' ' + t('cercetare', 'oficial').toUpperCase() + ' ', fontSize: 7, bold: true, color: '#14532D', background: VERDE_DESCHIS }, '  ' + (f.text || '')] },
          sursa(f),
        ].filter(Boolean),
        margin: [0, 0, 0, 6],
        unbreakable: true,
      })),
    });
  }

  if (lista(k.unghiuri).length) {
    C.push(...titlu(t('cercetare', 'unghiuri')));
    C.push({ ol: k.unghiuri.map(u => ({ text: u, margin: [0, 0, 0, 5] })) });
  }

  if (lista(k.riscuri).length) {
    C.push(chenar([
      ...titlu(t('cercetare', 'riscuri'), '#7A4508'),
      { ul: k.riscuri.map(u => ({ text: u, margin: [0, 0, 0, 4] })) },
    ], '#FFF7ED', '#F1D9B5'));
  }

  if (lista(k.pe_inteles).length) {
    C.push(...titlu(t('cercetare', 'peInteles')));
    k.pe_inteles.forEach(p => C.push({
      stack: [
        { text: [{ text: p.termen || '', bold: true }, `: ${p.explicatie || ''}`] },
        typeof p.in_postare === 'boolean'
          ? { text: t('cercetare', p.in_postare ? 'inPostareDa' : 'inPostareNu') + (p.de_ce ? ' ' + p.de_ce : ''), fontSize: 8, color: GRI_TEXT, margin: [0, 1, 0, 0] }
          : null,
      ].filter(Boolean),
      margin: [0, 0, 0, 6], unbreakable: true,
    }));
  }

  if (lista(k.din_dosar).length) {
    const STIL = { se_potriveste: ['#14532D', VERDE_DESCHIS], atentie: ['#7A4508', '#FFF4E5'], corectat: ['#1E3A8A', '#E8EEFB'] };
    C.push(...titlu(t('cercetare', 'dinDosar')));
    k.din_dosar.forEach(d => {
      const [cul, fund] = STIL[d.stare] || STIL.corectat;
      C.push({
        stack: [
          { text: [{ text: ' ' + t('cercetare', 'dosar_' + d.stare).toUpperCase() + ' ', fontSize: 7, bold: true, color: cul, background: fund }, '  ' + (d.text || '')] },
          d.corectura ? { text: `${t('cercetare', 'corectura')} ${d.corectura}`, fontSize: 8, color: GRI_TEXT, margin: [0, 1, 0, 0] } : null,
          d.fisier ? { text: d.fisier, fontSize: 7.5, color: '#6B7280' } : null,
        ].filter(Boolean),
        margin: [0, 0, 0, 6], unbreakable: true,
      });
    });
  }

  if (lista(k.actori).length) {
    C.push(...titlu(t('cercetare', 'actori')));
    k.actori.forEach(a => C.push({
      stack: [{ text: [{ text: a.nume || '', bold: true }, `: ${a.pozitie || ''}`] }, sursa(a)].filter(Boolean),
      margin: [0, 0, 0, 6], unbreakable: true,
    }));
  }

  if (lista(k.poze).length) {
    C.push(...titlu(t('cercetare', 'poza')));
    k.poze.forEach(p => {
      const link = https(p.link);
      C.push({
        stack: [
          { text: p.descriere || '', color: link ? VERDE : undefined, link: link || undefined },
          (p.drept || p.sursa) ? { text: [p.drept, p.sursa].filter(Boolean).join(' · '), fontSize: 8, color: GRI_TEXT } : null,
          p.neconfirmat ? { text: `${t('cercetare', 'neconfirmat')} ${p.neconfirmat}`, fontSize: 8, color: '#7A4508' } : null,
        ].filter(Boolean),
        margin: [0, 0, 0, 6], unbreakable: true,
      });
    });
  }

  if (lista(k.deoparte).length) {
    C.push(chenar([
      ...titlu(t('cercetare', 'deoparte'), '#374151'),
      { ul: k.deoparte.map(d => ({ text: [d.text || '', { text: ` (${d.motiv || ''})`, fontSize: 8, bold: true, color: '#6B7280' }], margin: [0, 0, 0, 4] })) },
    ], '#F3F4F6', null));
  }

  return {
    info: { title: `Cercetare ${client.nume} tema ${nr}`, author: 'New Mind · NM Social' },
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 44],
    defaultStyle: { font: 'Roboto', fontSize: 9.5, lineHeight: 1.25, color: '#111827' },
    footer: (pag, total) => ({
      columns: [{ text: 'NM Social · New Mind', color: '#6B7280' }, { text: `${pag} / ${total}`, alignment: 'right', color: '#6B7280' }],
      fontSize: 7.5, margin: [40, 14, 40, 0],
    }),
    content: C,
  };
}

// Face PDF-ul ca fisier. pdfmake si fonturile se incarca doar acum.
export async function fisierPdf(date) {
  const [{ default: pdfMake }, { default: vfs }] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  pdfMake.addVirtualFileSystem(vfs);
  pdfMake.setFonts({
    Roboto: { normal: 'Roboto-Regular.ttf', bold: 'Roboto-Medium.ttf', italics: 'Roboto-Italic.ttf', bolditalics: 'Roboto-MediumItalic.ttf' },
  });
  const blob = await pdfMake.createPdf(definitiePdf(date)).getBlob();
  return new File([blob], numePdf(date.client.nume, date.nr), { type: 'application/pdf' });
}

// Trimite fisierul prin fereastra de partajare a sistemului (WhatsApp, mail…).
// Intoarce 'trimis', 'anulat', 'din_nou' (browserul cere inca un click: fisierul e gata) sau 'descarcat'.
export async function partajeazaPdf(fisier) {
  if (navigator.canShare && navigator.canShare({ files: [fisier] })) {
    try {
      await navigator.share({ files: [fisier], title: fisier.name });
      return 'trimis';
    } catch (e) {
      if (e && e.name === 'AbortError') return 'anulat';
      if (e && e.name === 'NotAllowedError') return 'din_nou';
    }
  }
  const url = URL.createObjectURL(fisier);
  const a = document.createElement('a');
  a.href = url; a.download = fisier.name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return 'descarcat';
}
